import React, { useState } from 'react';
import { useKPIs } from '../../context/KPIContext';
import { KPIBucket } from '../../data/types';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Sparkles, Trash2, Pencil, Save, X, Sliders, Check } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import clsx from 'clsx';
export function KPIBuilder() {
  const {
    kpis,
    addKPI,
    updateKPI,
    deleteKPI,
    generateSignals
  } = useKPIs();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // New KPI Form State
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newSignals, setNewSignals] = useState<string[]>([]);
  const [isGeneratingNew, setIsGeneratingNew] = useState(false);
  // Edit KPI Form State
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSignals, setEditSignals] = useState<string[]>([]);
  const [isGeneratingEdit, setIsGeneratingEdit] = useState(false);
  const handleGenerateNew = async () => {
    if (!newName) {
      toast.error('Please enter a KPI name first');
      return;
    }
    setIsGeneratingNew(true);
    try {
      const signals = await generateSignals(newName);
      setNewSignals(signals);
      toast.success('Signals generated successfully');
    } catch (error) {
      toast.error('Failed to generate signals');
    } finally {
      setIsGeneratingNew(false);
    }
  };
  const handleSaveNew = () => {
    if (!newName || !newDesc) {
      toast.error('Name and description are required');
      return;
    }
    addKPI({
      name: newName,
      description: newDesc,
      signals: newSignals
    });
    setNewName('');
    setNewDesc('');
    setNewSignals([]);
    setIsAdding(false);
    toast.success('New KPI created');
  };
  const startEditing = (kpi: KPIBucket) => {
    setEditingId(kpi.id);
    setEditName(kpi.name);
    setEditDesc(kpi.description);
    setEditSignals(kpi.signals || []);
  };
  const handleGenerateEdit = async () => {
    if (!editName) return;
    setIsGeneratingEdit(true);
    try {
      const signals = await generateSignals(editName);
      setEditSignals(signals);
      toast.success('Signals updated');
    } finally {
      setIsGeneratingEdit(false);
    }
  };
  const handleSaveEdit = (id: string) => {
    updateKPI(id, {
      name: editName,
      description: editDesc,
      signals: editSignals
    });
    setEditingId(null);
    toast.success('KPI updated successfully');
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this KPI?')) {
      deleteKPI(id);
      toast.success('KPI deleted');
    }
  };
  return <div className="space-y-8 pb-20 relative" data-id="element-222">
      <Toaster position="top-right" theme="dark" data-id="element-223" />

      <header className="flex justify-between items-end" data-id="element-224">
        <div data-id="element-225">
          <h1 className="text-3xl font-bold text-white mb-2" data-id="element-226">
            KPI Configuration
          </h1>
          <p className="text-secondary" data-id="element-227">
            Define and manage organizational health metrics.
          </p>
        </div>
        {!isAdding && <button onClick={() => setIsAdding(true)} className="flex items-center space-x-2 bg-accent text-background px-4 py-2 rounded-lg font-medium hover:bg-accentHover transition-colors shadow-lg shadow-accent/10" data-id="element-228">
            <Plus className="w-4 h-4" data-id="element-229" />
            <span data-id="element-230">Add New KPI</span>
          </button>}
      </header>

      {/* Add New KPI Form */}
      <AnimatePresence data-id="element-231">
        {isAdding && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="glass-panel p-6 rounded-xl border border-accent/30" data-id="element-232">
            <div className="flex justify-between items-start mb-6" data-id="element-233">
              <h3 className="text-lg font-semibold text-white" data-id="element-234">
                New KPI Definition
              </h3>
              <button onClick={() => setIsAdding(false)} className="text-secondary hover:text-white" data-id="element-235">
                <X className="w-5 h-5" data-id="element-236" />
              </button>
            </div>

            <div className="space-y-4" data-id="element-237">
              <div data-id="element-238">
                <label className="block text-xs font-medium text-secondary mb-1" data-id="element-239">
                  KPI Name
                </label>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Innovation & Risk Taking" className="w-full bg-[#1A1D27] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all" data-id="element-240" />
              </div>

              <div data-id="element-241">
                <label className="block text-xs font-medium text-secondary mb-1" data-id="element-242">
                  Description
                </label>
                <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Describe what this KPI measures..." rows={2} className="w-full bg-[#1A1D27] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all resize-none" data-id="element-243" />
              </div>

              <div data-id="element-244">
                <div className="flex justify-between items-center mb-2" data-id="element-245">
                  <label className="block text-xs font-medium text-secondary" data-id="element-246">
                    Signal Examples
                  </label>
                  <button onClick={handleGenerateNew} disabled={isGeneratingNew || !newName} className="flex items-center space-x-1 text-xs text-accent hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-247">
                    <Sparkles className={clsx('w-3 h-3', isGeneratingNew && 'animate-spin')} data-id="element-248" />
                    <span data-id="element-249">
                      {isGeneratingNew ? 'Generating...' : 'Generate with AI'}
                    </span>
                  </button>
                </div>

                <div className="min-h-[60px] p-3 bg-[#1A1D27] rounded-lg border border-white/5 flex flex-wrap gap-2" data-id="element-250">
                  {newSignals.length > 0 ? newSignals.map((sig, i) => <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20" data-id="element-251">
                        {sig}
                      </span>) : <span className="text-xs text-secondary/50 italic" data-id="element-252">
                      AI-generated signals will appear here...
                    </span>}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4" data-id="element-253">
                <button onClick={() => setIsAdding(false)} className="px-4 py-2 text-sm text-secondary hover:text-white transition-colors" data-id="element-254">
                  Cancel
                </button>
                <button onClick={handleSaveNew} className="px-4 py-2 bg-accent text-background rounded-lg text-sm font-medium hover:bg-accentHover transition-colors shadow-lg shadow-accent/10" data-id="element-255">
                  Create KPI
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* KPI List */}
      <div className="grid grid-cols-1 gap-6" data-id="element-256">
        {kpis.map((kpi, idx) => <motion.div key={kpi.id} layout initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.05
      }} className={clsx('glass-card p-6 rounded-xl relative group transition-all duration-300', editingId === kpi.id ? 'border-accent/30 bg-white/5' : 'hover:border-white/10')} data-id="element-257">
            {editingId === kpi.id ?
        // Edit Mode
        <div className="space-y-4" data-id="element-258">
                <div className="flex justify-between items-start" data-id="element-259">
                  <div className="flex-1 mr-4" data-id="element-260">
                    <label className="block text-xs font-medium text-secondary mb-1" data-id="element-261">
                      Name
                    </label>
                    <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="w-full bg-[#1A1D27] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent" data-id="element-262" />
                  </div>
                  <div className="flex space-x-2" data-id="element-263">
                    <button onClick={() => handleSaveEdit(kpi.id)} className="p-2 bg-success/10 text-success rounded-lg hover:bg-success/20 transition-colors" title="Save Changes" data-id="element-264">
                      <Save className="w-4 h-4" data-id="element-265" />
                    </button>
                    <button onClick={() => setEditingId(null)} className="p-2 bg-white/5 text-secondary rounded-lg hover:bg-white/10 transition-colors" title="Cancel" data-id="element-266">
                      <X className="w-4 h-4" data-id="element-267" />
                    </button>
                  </div>
                </div>

                <div data-id="element-268">
                  <label className="block text-xs font-medium text-secondary mb-1" data-id="element-269">
                    Description
                  </label>
                  <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={2} className="w-full bg-[#1A1D27] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent resize-none" data-id="element-270" />
                </div>

                <div data-id="element-271">
                  <div className="flex justify-between items-center mb-2" data-id="element-272">
                    <label className="block text-xs font-medium text-secondary" data-id="element-273">
                      Signals
                    </label>
                    <button onClick={handleGenerateEdit} disabled={isGeneratingEdit} className="flex items-center space-x-1 text-xs text-accent hover:text-white disabled:opacity-50 transition-colors" data-id="element-274">
                      <Sparkles className={clsx('w-3 h-3', isGeneratingEdit && 'animate-spin')} data-id="element-275" />
                      <span data-id="element-276">Regenerate</span>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-277">
                    {editSignals?.map((sig, i) => <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-white/5 text-gray-300 border border-white/10" data-id="element-278">
                        {sig}
                        <button onClick={() => setEditSignals(prev => prev.filter((_, idx) => idx !== i))} className="ml-1.5 hover:text-danger" data-id="element-279">
                          <X className="w-3 h-3" data-id="element-280" />
                        </button>
                      </span>)}
                  </div>
                </div>
              </div> :
        // View Mode
        <>
                <div className="flex justify-between items-start mb-4" data-id="element-281">
                  <div data-id="element-282">
                    <h3 className="text-lg font-semibold text-white flex items-center" data-id="element-283">
                      {kpi.name}
                      <span className="ml-3 text-xs font-normal text-secondary bg-white/5 px-2 py-0.5 rounded-full border border-white/5" data-id="element-284">
                        Current Score:{' '}
                        <span className="text-white font-bold" data-id="element-285">
                          {kpi.score}
                        </span>
                      </span>
                    </h3>
                    <p className="text-sm text-secondary mt-1 max-w-2xl" data-id="element-286">
                      {kpi.description}
                    </p>
                  </div>
                  <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-287">
                    <button onClick={() => startEditing(kpi)} className="p-2 text-secondary hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-id="element-288">
                      <Pencil className="w-4 h-4" data-id="element-289" />
                    </button>
                    <button onClick={() => handleDelete(kpi.id)} className="p-2 text-secondary hover:text-danger hover:bg-danger/10 rounded-lg transition-colors" data-id="element-290">
                      <Trash2 className="w-4 h-4" data-id="element-291" />
                    </button>
                  </div>
                </div>

                {kpi.signals && kpi.signals.length > 0 && <div className="mt-4 pt-4 border-t border-white/5" data-id="element-292">
                    <h4 className="text-xs font-medium text-secondary uppercase tracking-wider mb-3" data-id="element-293">
                      Signal Examples
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-294">
                      {kpi.signals.map((sig, i) => <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs text-gray-300 bg-white/5 border border-white/5" data-id="element-295">
                          {sig}
                        </span>)}
                    </div>
                  </div>}
              </>}
          </motion.div>)}
      </div>
    </div>;
}